import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { LanguageId, VolunteeringData, VolunteerSelectedLanguageEnum, VolunteerSignupMentor, VolunteerSignupMentorStatusEnum } from '@interfaces';
import { VolunteeringAreaService } from '../../../volunteering-area.service';

interface MentorStatusCount {
	requiredMentors: LanguageCounts,
	pendingMentors: Record<VolunteerSelectedLanguageEnum, number>,
	assignedMentors: LanguageCounts,
}
interface LanguageCounts {
	english: number,
	spanish: number,
}

@Component({
	selector: 'volunteering-mentors-tab-1-part',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './volunteering-mentors-tab-1.part.html',
})

export class VolunteeringMentorsTab1Part {

	private readonly MENTORS_PER_ACCELERATOR = 14;

	private areaService = inject(VolunteeringAreaService);

	public mentorStatusCount = computed<MentorStatusCount | undefined>(() => {
		const data = this.areaService.volunteeringData();
		if (!data) return undefined;

		const signUpMentors = data.signUpMentors.filter(m => m.trimesterId === data.selectedTrimester.trimesterId);

		return {
			requiredMentors: this.calculateRequiredMentors(data),
			pendingMentors: this.calculatePendingMentors(signUpMentors),
			assignedMentors: this.calculateAssignedMentors(signUpMentors)
		};
	});

	private calculateRequiredMentors(data: VolunteeringData): LanguageCounts {
		return {
			english: data.acceleratorsCount.english * this.MENTORS_PER_ACCELERATOR,
			spanish: data.acceleratorsCount.spanish * this.MENTORS_PER_ACCELERATOR,
		};
	}

	private calculatePendingMentors(data: VolunteerSignupMentor[]): Record<VolunteerSelectedLanguageEnum, number> {
		return data
			.filter(m => m.volunteerSignupMentorStatus === VolunteerSignupMentorStatusEnum.Pending)
			.reduce((map, mentor) => {
				const key = mentor.selectedLanguage;
				map[key]++;
				return map;
			}, { [VolunteerSelectedLanguageEnum.EN]: 0, [VolunteerSelectedLanguageEnum.ES]: 0, [VolunteerSelectedLanguageEnum.BOTH]: 0 });
	}

	private calculateAssignedMentors(data: VolunteerSignupMentor[]): LanguageCounts {
		return data
			.filter(m => m.volunteerSignupMentorStatus === VolunteerSignupMentorStatusEnum.Assigned)
			.reduce((map, mentor) => {
				if (mentor.assignedLanguageId === LanguageId.English) {
					map.english++;
				} else if (mentor.assignedLanguageId === LanguageId.Espanol) {
					map.spanish++;
				}
				return map;
			}, { english: 0, spanish: 0 });
	}
}
